/**
 * Admin → Security · Active Throttles widget
 * ───────────────────────────────────────────
 * Surfaces the brute-force lockout state on the admin Security dashboard
 * with one-click unlock for any throttled email or IP.
 *
 * Visual language: Palantir Gotham — sharp 1px borders, monospace
 * readouts, bracketed section headers, ASCII-bar fills. Matches the
 * surrounding AdminSecurity page so it doesn't look bolted on.
 *
 * Data source: /api/auth/security/brute-force-summary (admin-only).
 * Auto-refreshes every 30s so incident response is real-time without
 * needing a manual reload.
 */
import React, { useEffect, useState } from 'react';
import { ShieldAlert, RefreshCw, X, Clock, AlertTriangle } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

/* eslint-disable -- v6 react-hooks/set-state-in-effect false-positive on plain async fetches. */
export default function BruteForceWidget({ isDark = true }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clearing, setClearing] = useState(null); // identifier currently being unlocked
  const [tick, setTick] = useState(0);            // bumps every 30s to trigger refresh
  // `now` lives in state so render stays pure (no Date.now() inside JSX).
  // Re-stamped on every refresh tick so the "minutes remaining" stays fresh.
  const [now, setNow] = useState(() => Date.now());

  const refresh = () => {
    setLoading(true);
    api.get('/auth/security/brute-force-summary')
      .then(({ data: d }) => setData(d))
      .catch((e) => {
        if (e.response?.status === 403) {
          // Component shown to non-admin — fail silent.
          setData({ active_lockouts: 0, top_throttled: [], thresholds: null, failed_logins_last_24h: 0, lockouts_last_30d: 0 });
        } else {
          toast.error(e.response?.data?.detail || 'Failed to load brute-force summary');
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { refresh(); }, [tick]);
  // Auto-refresh every 30s — incident-response surface should be live.
  // We bump both `tick` (re-fetches data) and `now` (re-stamps "minutes
  // remaining" without needing Date.now() inside the render).
  useEffect(() => {
    const id = setInterval(() => {
      setTick((t) => t + 1);
      setNow(Date.now());
    }, 30000);
    return () => clearInterval(id);
  }, []);
  /* eslint-enable */

  const unlock = async (identifier_type, identifier) => {
    const key = `${identifier_type}:${identifier}`;
    setClearing(key);
    try {
      const { data: r } = await api.post('/auth/security/unlock', { identifier_type, identifier });
      toast.success(`Cleared lockout for ${identifier}.`, {
        description: `${r.cleared} row${r.cleared === 1 ? '' : 's'} removed.`,
      });
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Unlock failed.');
    } finally {
      setClearing(null);
    }
  };

  const t = data?.thresholds || {};
  const top = data?.top_throttled || [];

  return (
    <div className="pal-card pal-card-live pal-grid-bg p-5" data-testid="admin-brute-force-widget">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <ShieldAlert size={13} className="text-rose-300" />
          <span className="pal-bracket">[ACTIVE THROTTLES · BRUTE-FORCE]</span>
        </div>
        <div className="flex items-center gap-2">
          {data && (
            <span className={`pal-status ${data.active_lockouts > 0 ? 'pal-status-warn' : 'pal-status-ok'}`}>
              {data.active_lockouts > 0 ? `${data.active_lockouts} LOCKED` : 'CLEAR'}
            </span>
          )}
          <button
            onClick={refresh}
            disabled={loading}
            data-testid="brute-force-widget-refresh"
            className={`px-2.5 py-1.5 text-[10px] uppercase tracking-[0.22em] rounded border pal-mono font-bold ${
              isDark ? 'border-white/10 text-slate-300 hover:bg-white/5' : 'border-slate-200 text-slate-600 hover:bg-slate-50'
            } disabled:opacity-50 flex items-center gap-1`}
          >
            <RefreshCw size={10} className={loading ? 'animate-spin' : ''} />
            REFRESH
          </button>
        </div>
      </div>

      {!data ? (
        <div className="text-sm opacity-60">Loading…</div>
      ) : (
        <>
          {/* Top-level data readouts */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            <Readout
              label="ACTIVE NOW"
              value={data.active_lockouts}
              accent={data.active_lockouts > 0 ? 'rose' : 'emerald'}
              isDark={isDark}
            />
            <Readout
              label="FAILED · 24H"
              value={data.failed_logins_last_24h?.toLocaleString?.() ?? 0}
              accent="slate"
              isDark={isDark}
            />
            <Readout
              label="LOCKOUTS · 30D"
              value={data.lockouts_last_30d?.toLocaleString?.() ?? 0}
              accent="slate"
              isDark={isDark}
            />
            <Readout
              label="WINDOW"
              value={t.window_minutes ? `${t.window_minutes}M / ${t.lockout_minutes}M` : '—'}
              accent="slate"
              isDark={isDark}
              small
            />
          </div>

          {/* Policy strip */}
          {t.email_max_attempts && (
            <div className={`mb-5 px-3 py-2 rounded border flex items-center gap-3 text-[11px] pal-mono ${
              isDark ? 'border-white/10 bg-white/[0.02] text-slate-300' : 'border-slate-200 bg-slate-50 text-slate-600'
            }`}>
              <Clock size={11} className="opacity-60 shrink-0" />
              <span><span className="opacity-60">PER-EMAIL</span> {t.email_max_attempts}/{t.window_minutes}m</span>
              <span className="opacity-30">·</span>
              <span><span className="opacity-60">PER-IP</span> {t.ip_max_attempts}/{t.window_minutes}m</span>
              <span className="opacity-30">·</span>
              <span><span className="opacity-60">LOCKOUT</span> {t.lockout_minutes}m</span>
            </div>
          )}

          {/* Top throttled list */}
          {top.length === 0 ? (
            <div className={`px-4 py-6 rounded border-dashed border text-center ${
              isDark ? 'border-white/10 text-slate-400' : 'border-slate-200 text-slate-500'
            }`}>
              <ShieldAlert size={16} className="inline mb-1 opacity-50" />
              <div className="text-[11px] pal-mono uppercase tracking-[0.18em] mt-1">No active lockouts.</div>
              <div className="text-[10px] opacity-60 mt-1">All clear · brute-force shield watching.</div>
            </div>
          ) : (
            <div className="space-y-1" data-testid="brute-force-widget-list">
              <div className="grid grid-cols-12 gap-2 px-3 py-1.5 pal-mono text-[9px] uppercase tracking-[0.18em] opacity-60">
                <div className="col-span-1">TYPE</div>
                <div className="col-span-5">IDENTIFIER</div>
                <div className="col-span-2 text-right">FAILS</div>
                <div className="col-span-2">UNLOCKS</div>
                <div className="col-span-2 text-right">ACTION</div>
              </div>
              {top.map((row, i) => {
                const key = `${row.identifier_type}:${row.identifier}`;
                const isClearing = clearing === key;
                const luMs = row.locked_until ? new Date(row.locked_until).getTime() : 0;
                const minsRemaining = luMs ? Math.max(0, Math.ceil((luMs - now) / 60000)) : 0;
                return (
                  <div
                    key={`${key}-${i}`}
                    className={`grid grid-cols-12 gap-2 px-3 py-2 rounded items-center text-[11px] pal-mono border ${
                      isDark ? 'border-white/5 bg-rose-500/[0.04] hover:bg-rose-500/[0.08]' : 'border-slate-100 bg-rose-50/40 hover:bg-rose-50'
                    } transition-colors`}
                    data-testid={`brute-force-widget-row-${i}`}
                  >
                    <div className="col-span-1">
                      <span className={`px-1.5 py-0.5 rounded text-[9px] uppercase font-bold ${
                        row.identifier_type === 'email'
                          ? 'bg-rose-500/15 text-rose-300'
                          : 'bg-amber-500/15 text-amber-300'
                      }`}>
                        {row.identifier_type === 'email' ? 'EM' : 'IP'}
                      </span>
                    </div>
                    <div className="col-span-5 truncate" title={row.identifier}>
                      {row.identifier}
                    </div>
                    <div className="col-span-2 text-right tabular-nums opacity-80">
                      {row.attempts_in_window}
                    </div>
                    <div className="col-span-2 tabular-nums opacity-80 flex items-center gap-1">
                      <Clock size={9} className="opacity-50" />
                      {minsRemaining}m
                    </div>
                    <div className="col-span-2 flex justify-end">
                      <button
                        onClick={() => unlock(row.identifier_type, row.identifier)}
                        disabled={isClearing}
                        data-testid={`brute-force-widget-unlock-${i}`}
                        title={`Clear lockout for ${row.identifier}`}
                        className={`px-2 py-1 text-[9px] uppercase tracking-[0.18em] font-bold rounded border transition-colors flex items-center gap-1 ${
                          isDark
                            ? 'border-rose-400/30 text-rose-300 hover:bg-rose-500/15 hover:border-rose-400/60'
                            : 'border-rose-200 text-rose-600 hover:bg-rose-100'
                        } disabled:opacity-50`}
                      >
                        {isClearing ? <RefreshCw size={9} className="animate-spin" /> : <X size={9} />}
                        UNLOCK
                      </button>
                    </div>
                  </div>
                );
              })}
              {data.active_lockouts > top.length && (
                <div className={`text-[10px] pal-mono opacity-60 text-center py-2 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                  Showing top {top.length} of {data.active_lockouts}. Older lockouts auto-expire after {t.lockout_minutes}m.
                </div>
              )}
            </div>
          )}

          {/* Operator hint */}
          <div className={`mt-4 px-3 py-2 rounded text-[10px] leading-relaxed flex items-start gap-2 ${
            isDark ? 'bg-white/[0.02] text-slate-400 border border-white/5' : 'bg-slate-50 text-slate-500 border border-slate-100'
          }`}>
            <AlertTriangle size={11} className="mt-0.5 opacity-50 shrink-0" />
            <span>
              Unlocking is audited — every <code className="pal-mono opacity-80">auth_lockout_cleared</code> event flows into the immutable audit log with your email + role.
            </span>
          </div>
        </>
      )}
    </div>
  );
}

/* ── Compact Bloomberg-style readout (mirrors AdminSecurity.DataReadout) ── */
function Readout({ label, value, accent, isDark, small }) {
  const accents = {
    emerald: isDark
      ? { fg: 'text-emerald-300', bg: 'bg-emerald-500/10 border-emerald-500/30' }
      : { fg: 'text-emerald-700', bg: 'bg-emerald-50 border-emerald-200' },
    rose: isDark
      ? { fg: 'text-rose-300',    bg: 'bg-rose-500/10 border-rose-500/30' }
      : { fg: 'text-rose-700',    bg: 'bg-rose-50 border-rose-200' },
    slate: isDark
      ? { fg: 'text-slate-200',   bg: 'bg-white/[0.03] border-white/10' }
      : { fg: 'text-slate-700',   bg: 'bg-slate-50 border-slate-200' },
  };
  const a = accents[accent] || accents.slate;
  return (
    <div className={`p-3 rounded-md border ${a.bg}`}>
      <div className="pal-mono text-[9px] uppercase tracking-[0.20em] opacity-70 mb-1">{label}</div>
      <div className={`pal-mono ${small ? 'text-sm' : 'text-2xl'} tabular-nums font-bold ${a.fg}`}>
        {value}
      </div>
    </div>
  );
}
